import React, { useState, useCallback } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Box,
  FormHelperText,
  OutlinedInput
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import { toast } from "react-toastify"; 
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import { useMediaQuery, useTheme } from "@mui/material";
import { compressImage } from '../utils/compressImage';

import {
  ACCOMMODATION_TYPES,
  ROOM_AMENITIES,
  Accommodation,
} from "../types/property";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
  getStorage,
} from "firebase/storage";
//import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from "../firebaseconfig";
const BASIC_ROOM_NAMES = [
  "Standard Room",
  "Deluxe Room",
  "Superior Room",
  "Executive Room",
  "Family Room",
  "Twin Room",
  "Single Room",
  "Double Room",
  "Suite",
  "Presidential Suite",
  "Dorm Bed",
  "Private Cottage",
  "Luxury Villa",
    "4 Bed Dorm", "6 Bed Dorm", "8 Bed Dorm", "10 Bed Dorm",
  "12 Bed Dorm", "14 Bed Dorm", "16 Bed Dorm", "20 Bed Dorm",
  "Mixed Dormitory", "Female Dorm", "Male Dorm",
  "Dorm Bed (Shared)", "Bunk Bed Dorm",
];

const BASIC_ROOM_BED_CONFIGURATIONS = [
  "1 King Bed",
  "1 Queen Bed", 
  "2 Single Beds",
  "1 King + 1 Queen",
  "1 King + 1 Sofa Bed",
  "2 Queen Beds",
  "1 Double Bed",
  "Custom",
];


const DORMITORY_BED_CONFIGURATIONS = [
  "Single Bed",
  "Twin XL Bed",
  "Bunk Bed (Lower)",
  "Bunk Bed (Upper)", 
  "Bunk Beds (2-Tier)",
  "Bunk Beds (3-Tier)",
  "Pod Bed",
  "Capsule Bed",
  "Custom",
];



const AccommodationsSection = ({ formData, onChange }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentAccommodation, setCurrentAccommodation] = useState({
    type: "room",
    name: "",
    description: "",
    quantity: 1,
    maxOccupancy: 2,
    bedConfiguration: "",
    size: {
  width: '',
  length: '',
  area: 0
},
    pricePerNightB2B: 0, 
    pricePerNightB2C: 0,
      mealPlanPricing: {
    CP:  { B2B: '', B2C: '' },
    MAP: { B2B: '', B2C: '' },
    AP:  { B2B: '', B2C: '' }
  },
    amenities: [],
    images: [],
    isSharedRoom: false,
  bedsPerRoom: 1,
  genderRestriction: 'any',
  bedPrivacy: '',
  commonBathroom: false,
  });
  const [dialogErrors, setDialogErrors] = useState({});
const [infoAnchorEl, setInfoAnchorEl] = useState(null);
const [currentInfoKey, setCurrentInfoKey] = useState(null);
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

const [showCustomNameField, setShowCustomNameField] = useState(false);

const INFO_CONTENT = {
  // Core fields
  type: "Select accommodation category. This affects availability and pricing display.",
  name: "Choose predefined name or 'Custom Name' for unique room types.",
  description: "Brief description (max 500 chars) shown to customers on booking page.",
  quantity: "Number of rooms/dormitory of this specific type available. Example: If you have 5 Deluxe Rooms, enter 5.",
  maxOccupancy: "Maximum guests allowed per room/bed, regardless of bed count.",

  // Dormitory-specific 
  bedsPerRoom: "Number of beds in each dormitory room. Typically 4-20 for hostels.",
  genderRestriction: "Specify who can book this dormitory. 'Mixed' allows both genders.",
  bedPrivacy: "Privacy level between beds. Curtains are most common in budget hostels.",

  // Bed & Size
  bedConfiguration: "Exact bed setup shown to customers. Use 'Custom' for unique setups.",
  size: "Room dimensions in feet. Area auto-calculates as Width × Length.",

  // Pricing
  pricePerNightB2B: "B2B price for travel agents/partners (usually lower than B2C).",
  pricePerNightB2C: "Retail price for direct customers (higher than B2B).",

  // Images & Amenities
  images: "Upload 5-30 high-quality photos. First image becomes primary display.",
  amenities: "Select all available facilities. Shown as icons on booking page.",
    mealPlanPricing: "Optional meal plan prices. Leave blank if not offered. Must be higher than EP (room-only) price.",
  CP:  "Continental Plan — Room + Breakfast. Set B2B and B2C prices if offered.",
  MAP: "Modified American Plan — Room + Breakfast + Dinner. Set B2B and B2C prices if offered.",
  AP:  "American Plan — Room + All meals (Breakfast, Lunch, Dinner). Set B2B and B2C prices if offered.",
};

const InfoIconButton = ({ fieldKey, placement = "top", size = "small" }) => (
  <IconButton
    size={size}
    onClick={isMobile ? (e) => {
      e.stopPropagation();
      setInfoAnchorEl(e.currentTarget);
      setCurrentInfoKey(fieldKey);
    } : undefined}
    onMouseEnter={!isMobile ? (e) => {
      setInfoAnchorEl(e.currentTarget);
      setCurrentInfoKey(fieldKey);
    } : undefined}
    onMouseLeave={!isMobile ? () => {
      setInfoAnchorEl(null);
      setCurrentInfoKey(null);
    } : undefined}
    sx={{
      color: "grey.500",
      '&:hover': { backgroundColor: "rgba(0,0,0,0.04)", color: "primary.main" },
      width: 28,
      height: 28,
      ml: 0.5,
    }}
  >
    <InfoIcon fontSize="small" />
  </IconButton>
);
  const accommodations = formData.accommodations || [];
 
  const validateAccommodationField = useCallback((field, value, accommodation = currentAccommodation) => {
    const errors = {};

    switch (field) {
      case "name":
        if (!value?.trim()) errors.name = "Accommodation name is required";
        else if (value.trim().length < 3)
          errors.name = "Name must be at least 3 characters";
        else if (value.trim().length > 50)
          errors.name = "Name cannot exceed 50 characters";
        break;

      case "description":
        if (value?.trim().length > 500)
          errors.description = "Description cannot exceed 500 characters"; 
        break;

      case "type":
        if (!value) errors.type = "Accommodation type is required";
        else if (
          ![
            "room",
            "cottage",
            "suite",
            "villa",
            "dormitory",
            "tent",
            "cabin",
            "apartment",
          ].includes(value)
        )
          errors.type = "Invalid accommodation type";
        break;

      case "quantity":
        if (!value || value < 1)
          errors.quantity = "Quantity must be at least 1"; 
        break;

      case "maxOccupancy":
        if (!value || value < 1)
          errors.maxOccupancy = "Max occupancy must be at least 1"; 
        break;

      case "bedConfiguration":
        if (!value?.trim())
          errors.bedConfiguration = "Bed configuration is required";
        break;

      case "pricePerNightB2B":
        if (!value || value < 500)
          errors.pricePerNightB2B = "B2B price must be at least ₹500";
        break;

      case "pricePerNightB2C":
        if (!value || value < 500)
          errors.pricePerNightB2C = "B2C price must be at least ₹500";
        break;
      
      case "bedsPerRoom":
        if (accommodation.type === 'dormitory') {
         if (!value || value < 2 || value > 24)
            errors.bedsPerRoom = "Dorms must have 2-24 beds";
        }
        break;

        case "mealPlanPricing":
  const epB2B = currentAccommodation.pricePerNightB2B;
  const epB2C = currentAccommodation.pricePerNightB2C;
  const plans = ['CP', 'MAP', 'AP'];

  plans.forEach((plan) => {
    const planB2B = currentAccommodation.mealPlanPricing?.[plan]?.B2B;
    const planB2C = currentAccommodation.mealPlanPricing?.[plan]?.B2C;

    if (planB2B && planB2B < epB2B) {
      errors[`mealPlan_${plan}_B2B`] = `${plan} B2B (₹${planB2B}) must be ≥ EP B2B (₹${epB2B})`;
    }
    if (planB2C && planB2C < epB2C) {
      errors[`mealPlan_${plan}_B2C`] = `${plan} B2C (₹${planB2C}) must be ≥ EP B2C (₹${epB2C})`;
    }
  });
  break;

    }

    return errors;
  }, []);


  const handleDialogChange = (field, value) => {
    setCurrentAccommodation((prev) => ({ ...prev, [field]: value }));

    
    const fieldErrors = validateAccommodationField(field, value);
    setDialogErrors((prev) => {
      const newErrors = { ...prev };
      if (Object.keys(fieldErrors).length === 0) {
        delete newErrors[field];
      } else {
        Object.assign(newErrors, fieldErrors);
      }
      return newErrors;
    });
  };

 const uploadImage = async (file) => {
    const fileToUpload =
      file.size > 300 * 1024
        ? await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.75 })
        : file;

    const storageRef = ref(
      storage,
      `accommodations/${Date.now()}_${fileToUpload.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, fileToUpload);
    return new Promise((resolve, reject) => {
      uploadTask.on("state_changed", null, reject, async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(url);
      });
    });
  };

  React.useEffect(() => {
   // console.log("Form data accommodations:", formData.accommodations);
  }, [formData.accommodations]);

  
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);

    if (currentAccommodation.images.length + files.length > 30) {
      alert("Maximum 30 images allowed");
      return;
    }

    try {
     
      const uploadedUrls = await Promise.all(files.map(uploadImage));

     
      const newImages = uploadedUrls.map((url) => ({
        url,
        caption: "",
        isPrimary: false,
      }));

     
      setCurrentAccommodation({
        ...currentAccommodation,
        images: [...currentAccommodation.images, ...newImages],
      });
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to upload images. Please try again.");
    }
  };
  const handleSaveAccommodation = async () => {
    const errors = {};

   
    const fields = [
      "name",
      "type",
      "quantity",
      "maxOccupancy",
      "bedConfiguration",
      "pricePerNightB2B",
      "pricePerNightB2C",
      "description",
       "mealPlanPricing",
    ];

    fields.forEach((field) => {
      const fieldErrors = validateAccommodationField(
        field,
        currentAccommodation[field]
      );
      Object.assign(errors, fieldErrors);
    });

    

    if (
      currentAccommodation.images.length < 5 ||
      currentAccommodation.images.length > 30
    ) {
      errors.images = "Must have between 5 and 30 images"; 
    }
    setDialogErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the errors before saving");
      return;
    }

    
    const sanitizedImages = currentAccommodation.images.map(
      ({ url, caption, isPrimary, order }) => ({
        url,
        caption,
        isPrimary,
        order,
      })
    );

    const newAccommodation = {
      ...currentAccommodation,
      images: sanitizedImages,
      id:
        editingIndex !== null
          ? accommodations[editingIndex].id
          : Date.now().toString(),
    };

    let updatedAccommodations;
    if (editingIndex !== null) {
      updatedAccommodations = [...accommodations];
      updatedAccommodations[editingIndex] = newAccommodation;
    } else {
      updatedAccommodations = [...accommodations, newAccommodation];
    }

    onChange("accommodations", updatedAccommodations);
    resetDialog();
    toast.success("Accommodation saved successfully!");
  };

  const handleEdit = (index) => {
    setCurrentAccommodation(accommodations[index]);
    setEditingIndex(index);
    setIsDialogOpen(true);
  };

  const handleDelete = (index) => {
    const updatedAccommodations = accommodations.filter((_, i) => i !== index);
    onChange("accommodations", updatedAccommodations);
  };

  const openDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingIndex(null);
    setCurrentAccommodation({
      type: "room",
      name: "",
      description: "",
      quantity: 1,
      maxOccupancy: 2,
      bedConfiguration: "",
      size: {
  width: '',
  length: '',
  area: 0
},
  isSharedRoom: false,
    bedsPerRoom: 1,
    genderRestriction: 'any',
    bedPrivacy: '',
    commonBathroom: false,
      pricePerNightB2B: 0,
      pricePerNightB2C: 0,
         mealPlanPricing: {
      CP:  { B2B: '', B2C: '' },
      MAP: { B2B: '', B2C: '' },
      AP:  { B2B: '', B2C: '' }
    },
      amenities: [],
      images: [],
    });
    setShowCustomNameField(false);
  };

  const resetDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const toggleAmenity = (amenity) => {
    const currentAmenities = currentAccommodation.amenities || [];
    const updatedAmenities = currentAmenities.includes(amenity)
      ? currentAmenities.filter((a) => a !== amenity)
      : [...currentAmenities, amenity];
    setCurrentAccommodation({
      ...currentAccommodation,
      amenities: updatedAmenities,
    });
  };
const handleSizeChange = (value) => {
  let width = '';
  let length = '';

  
  if (/^\d+\s*\*\s*\d+$/.test(value)) {
    const parts = value.split('*').map(v => parseFloat(v.trim()));
    width = parts[0];
    length = parts[1];
  }

  const area = width && length ? width * length : 0;

  setCurrentAccommodation({
    ...currentAccommodation,
    size: { width, length, area }
  });
};

  return (
    <Box className="space-y-6">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" color="primary">
            Accommodation Types
          </Typography>
        </Box>
        <Dialog
          open={isDialogOpen}
          onClose={resetDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingIndex !== null
              ? "Edit Accommodation"
              : "Add New Accommodation"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} marginTop={1}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="accType-label">
                    Accommodation Type *
                     <InfoIconButton fieldKey="type" />
                  </InputLabel>
                  <Select
                    labelId="accType-label"
                    label="Accommodation Type *"
                    value={currentAccommodation.type}
                    onChange={(e) =>
                      
                      setCurrentAccommodation({
                        ...currentAccommodation,
                        type: e.target.value,
                        maxOccupancy: e.target.value === 'dormitory' ? 1 : 2
                      })
                    }
                  >
                    {ACCOMMODATION_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                        
                  {dialogErrors.type && (
                    <FormHelperText error>{dialogErrors.type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
<Grid item xs={6}>
  <FormControl fullWidth error={Boolean(dialogErrors.name)}>
    <InputLabel id="room-name-label">Name *</InputLabel>
    <Select
      labelId="room-name-label"
      label="Name *"
      value={currentAccommodation.name}
      onChange={(e) => {
        const value = e.target.value;
        if (value === "custom") {
          setCurrentAccommodation({
            ...currentAccommodation,
            name: "custom"
          });
          setShowCustomNameField(true);  
        } else {
          setCurrentAccommodation({
            ...currentAccommodation,
            name: value
          });
          setShowCustomNameField(false); 
          handleDialogChange("name", value);
        }
      }}
    >
      {BASIC_ROOM_NAMES.map((name) => (
        <MenuItem key={name} value={name}>
          {name}
        </MenuItem>
      ))}
      <MenuItem value="custom">Custom Name</MenuItem>
    </Select>
    {dialogErrors.name && (
      <FormHelperText>{dialogErrors.name}</FormHelperText>
    )}
  </FormControl>
</Grid>

{/* ✅ CONTROLLED BY showCustomNameField */}
{showCustomNameField && (
  <Grid item xs={12}>
    <TextField
      label="Custom Room Name *"
      fullWidth
      value={currentAccommodation.name === "custom" ? "" : currentAccommodation.name}
      onChange={(e) => {
        const value = e.target.value;
        setCurrentAccommodation({
          ...currentAccommodation,
          name: value
        });
        handleDialogChange("name", value);
      }}
      error={Boolean(dialogErrors.name)}
      helperText={dialogErrors.name}
    />
  </Grid>
)}




              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={currentAccommodation.description}
                  onChange={(e) =>
                    setCurrentAccommodation({
                      ...currentAccommodation,
                      description: e.target.value,
                    })
                  }
                  multiline
                  rows={3}
                  fullWidth
                  error={Boolean(dialogErrors.description)}
                  helperText={
                    dialogErrors.description ||
                    (currentAccommodation.description
                      ? `${currentAccommodation.description.length}/500`
                      : "Max 500 characters")
                  }
                />

              </Grid>
               
              <Grid item xs={6}>
                <TextField
                   label={
                <>
                  Quantity Available* 
                  <InfoIconButton fieldKey="quantity" />
                </>
              }
                  type="number"
                  value={currentAccommodation.quantity}
                  onChange={(e) =>
                    setCurrentAccommodation({
                      ...currentAccommodation,
                      quantity: parseInt(e.target.value),
                    })
                  }
                  fullWidth
                  inputProps={{ min: 1 }}
                  error={Boolean(dialogErrors.quantity)}
                  helperText={dialogErrors.quantity}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                   label={
                <>
                  Max Occupancy* 
                  <InfoIconButton fieldKey="maxOccupancy" />
                </>
              }
                  type="number"
                  value={currentAccommodation.maxOccupancy}
                  onChange={(e) =>
                    setCurrentAccommodation({
                      ...currentAccommodation,
                      maxOccupancy: parseInt(e.target.value),
                    })
                  }
                  fullWidth
                  inputProps={{ min: 1 }}
                  error={Boolean(dialogErrors.maxOccupancy)}
                  helperText={dialogErrors.maxOccupancy}
                />
              </Grid>
              {currentAccommodation.type === 'dormitory' && (
  <>
    <Grid item xs={6}>
      <TextField
         label={
                    <>
                      Beds Per Room* 
                      <InfoIconButton fieldKey="bedsPerRoom" />
                    </>
                  }
        type="number"
        value={currentAccommodation.bedsPerRoom}
        onChange={(e) => {
  const value = parseInt(e.target.value) || 1;
  handleDialogChange("bedsPerRoom", value);
}}
        fullWidth
        inputProps={{ min: 2, max: 24 }}
        error={Boolean(dialogErrors.bedsPerRoom)}
        helperText={dialogErrors.bedsPerRoom || "2-24 beds"}
      />
    </Grid>
    <Grid item xs={6}>
      <FormControl fullWidth error={Boolean(dialogErrors.genderRestriction)}>
        <InputLabel>Gender Restriction <InfoIconButton fieldKey="genderRestriction" /></InputLabel>
        <Select
          value={currentAccommodation.genderRestriction}
          onChange={(e) => handleDialogChange("genderRestriction", e.target.value)}
          label="Gender Restriction"
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="male">Male Only</MenuItem>
          <MenuItem value="female">Female Only</MenuItem>
          <MenuItem value="mixed">Mixed</MenuItem>
        </Select>
      </FormControl>
    </Grid>
<Grid item xs={6}>
  <FormControl fullWidth variant="outlined">
  <InputLabel
  shrink
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    backgroundColor: (theme) => theme.palette.background.paper,
    px: 0.5,
  }}
>
  Bed Privacy
  <InfoIconButton fieldKey="bedPrivacy" />
</InputLabel>

  <Select
  value={currentAccommodation.bedPrivacy || ""}
  onChange={(e) =>
    handleDialogChange("bedPrivacy", e.target.value)
  }
  input={<OutlinedInput notched label="Bed Privacy" />}
>
      <MenuItem value="">
        <em>Select Bed Privacy</em>
      </MenuItem>
      <MenuItem value="none">None</MenuItem>
      <MenuItem value="curtains">Curtains</MenuItem>
      <MenuItem value="partition">Partition</MenuItem>
      <MenuItem value="private">Private</MenuItem>
    </Select>
  </FormControl>
</Grid>
  </>
)}

   <Grid item xs={6}>
<FormControl
  fullWidth
  error={Boolean(dialogErrors.bedConfiguration)}
  variant="outlined"
>
  <InputLabel
    id="bed-config-label"
   
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    Bed Configuration *
    <InfoIconButton fieldKey="bedConfiguration" />
  </InputLabel>

  <Select
    labelId="bed-config-label"
    label="Bed Configuration *"
    value={currentAccommodation.bedConfiguration}
    onChange={(e) =>
      handleDialogChange("bedConfiguration", e.target.value)
    }
  >
    <MenuItem value="single">Single Bed</MenuItem>
    <MenuItem value="double">Double Bed</MenuItem>
    <MenuItem value="twin">Twin Beds</MenuItem>
    <MenuItem value="bunk">Bunk Bed</MenuItem>
  </Select>
</FormControl>

</Grid>

{currentAccommodation.bedConfiguration === "Custom" && (
  <Grid item xs={12}>
    <TextField
      label="Custom Bed Configuration *"
      fullWidth
      value={currentAccommodation.bedConfigurationCustom || ""}
      onChange={(e) =>
        setCurrentAccommodation({
          ...currentAccommodation,
          bedConfigurationCustom: e.target.value,
          bedConfiguration: e.target.value,
        })
      }
    />
  </Grid>
)}


             <Grid item xs={3}>
  <TextField
     label={
                <>
                  Width of the Room (ft)* 
                  <InfoIconButton fieldKey="size" />
                </>
              }
    type="number"
    fullWidth
    value={currentAccommodation.size?.width || ''}
    onChange={(e) => {
      const width = parseFloat(e.target.value) || '';
      const length = currentAccommodation.size?.length || '';
      const area = width && length ? width * length : 0;

      setCurrentAccommodation({
        ...currentAccommodation,
        size: { width, length, area }
      });
    }}
  />
</Grid>

<Grid item xs={3}>
  <TextField
     label={
                <>
                  Length of the Room (ft)* 
                  <InfoIconButton fieldKey="size" />
                </>
              }
    type="number"
    fullWidth
    value={currentAccommodation.size?.length || ''}
    onChange={(e) => {
      const length = parseFloat(e.target.value) || '';
      const width = currentAccommodation.size?.width || '';
      const area = width && length ? width * length : 0;

      setCurrentAccommodation({
        ...currentAccommodation,
        size: { width, length, area }
      });
    }}
  />
</Grid>





              <Grid item xs={6}>
                <TextField
                   label={
                <>
                  Price Per Night B2B (₹)* 
                  <InfoIconButton fieldKey="pricePerNightB2B" />
                </>
              }
                  type="number"
                  value={currentAccommodation.pricePerNightB2B}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCurrentAccommodation((prev) => ({
                      ...prev,
                    
                      pricePerNightB2B: val === "" ? "" : Number(val),
                    }));
                  }}
                  fullWidth
                  inputProps={{ min: 0 }}
                  error={Boolean(dialogErrors.pricePerNightB2B)}
                  helperText={dialogErrors.pricePerNightB2B}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                    label={
                <>
                  Price Per Night B2C (₹)* 
                  <InfoIconButton fieldKey="pricePerNightB2C" />
                </>
              }
                  type="number"
                  value={currentAccommodation.pricePerNightB2C}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCurrentAccommodation((prev) => ({
                      ...prev,
                      pricePerNightB2C: val === "" ? "" : Number(val),
                    }));
                  }}
                  fullWidth
                  inputProps={{ min: 0 }}
                  error={Boolean(dialogErrors.pricePerNightB2C)}
                  helperText={dialogErrors.pricePerNightB2C}
                />
              </Grid>

{/* ─── Meal Plan Pricing ─────────────────────────────────────── */}
<Grid item xs={12}>
  <Typography variant="subtitle1" mb={1}>
    Meal Plan Pricing (Optional)
    <InfoIconButton fieldKey="mealPlanPricing" />
  </Typography>
  <Typography variant="body2" color="textSecondary" mb={2}>
    EP (Room Only) = B2B ₹{currentAccommodation.pricePerNightB2B} / B2C ₹{currentAccommodation.pricePerNightB2C}
  </Typography>
</Grid>

{[
  { key: 'CP',  label: 'CP - Room + Breakfast' },
  { key: 'MAP', label: 'MAP - Room + Breakfast + Dinner' },
  { key: 'AP',  label: 'AP - Room + All Meals' },
].map(({ key, label }) => (
  <React.Fragment key={key}>
    <Grid item xs={12}>
      <Typography variant="body2" fontWeight={600} color="text.secondary">
        {label}
        <InfoIconButton fieldKey={key} />
      </Typography>
    </Grid>

    <Grid item xs={6}>
      <TextField
        label={`${key} B2B Price (₹)`}
        type="number"
        fullWidth
        value={currentAccommodation.mealPlanPricing?.[key]?.B2B || ''}
        onChange={(e) => {
          const val = e.target.value === '' ? '' : Number(e.target.value);
          setCurrentAccommodation((prev) => ({
            ...prev,
            mealPlanPricing: {
              ...prev.mealPlanPricing,
              [key]: { ...prev.mealPlanPricing[key], B2B: val }
            }
          }));
        }}
        inputProps={{ min: currentAccommodation.pricePerNightB2B }}
        error={Boolean(dialogErrors[`mealPlan_${key}_B2B`])}
        helperText={
          dialogErrors[`mealPlan_${key}_B2B`] ||
          `Min ₹${currentAccommodation.pricePerNightB2B} (EP B2B)`
        }
      />
    </Grid>

    <Grid item xs={6}>
      <TextField
        label={`${key} B2C Price (₹)`}
        type="number"
        fullWidth
        value={currentAccommodation.mealPlanPricing?.[key]?.B2C || ''}
        onChange={(e) => {
          const val = e.target.value === '' ? '' : Number(e.target.value);
          setCurrentAccommodation((prev) => ({
            ...prev,
            mealPlanPricing: {
              ...prev.mealPlanPricing,
              [key]: { ...prev.mealPlanPricing[key], B2C: val }
            }
          }));
        }}
        inputProps={{ min: currentAccommodation.pricePerNightB2C }}
        error={Boolean(dialogErrors[`mealPlan_${key}_B2C`])}
        helperText={
          dialogErrors[`mealPlan_${key}_B2C`] ||
          `Min ₹${currentAccommodation.pricePerNightB2C} (EP B2C)`
        }
      />
    </Grid>
  </React.Fragment>
))}

              <Grid item xs={12}>
                <Typography variant="subtitle1" mb={1}>
                  Upload Images (min 5, max 30)
                   <InfoIconButton fieldKey="images" />
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                />
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {currentAccommodation.images.map((img, idx) => (
                    <Box key={idx} position="relative">
                      <img
                        src={img.url}
                        alt={`Image ${idx + 1}`}
                        style={{
                          width: 100,
                          height: 100,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                      <Button
                        size="small"
                        variant={img.isPrimary ? "contained" : "outlined"}
                        onClick={() => {
                          const updatedImages = currentAccommodation.images.map(
                            (image, i) => ({
                              ...image,
                              isPrimary: i === idx,
                            })
                          );
                          setCurrentAccommodation({
                            ...currentAccommodation,
                            images: updatedImages,
                          });
                        }}
                      >
                        {img.isPrimary ? "Primary" : "Set Primary"}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const updatedImages =
                            currentAccommodation.images.filter(
                              (_, i) => i !== idx
                            );
                          setCurrentAccommodation({
                            ...currentAccommodation,
                            images: updatedImages,
                          });
                        }}
                        style={{ position: "absolute", top: 0, right: 0 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
                {currentAccommodation.images.length < 5 && (
                  <Typography color="error" variant="body2" mt={1}>
                    Please upload at least 5 images.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">Room Amenities <InfoIconButton fieldKey="amenities" /></Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(2, 1fr)"
                  gap={1}
                  maxHeight={192}
                  overflow="auto"
                  border={1}
                  borderRadius={1}
                  p={2}
                >
                  {ROOM_AMENITIES.map((amenity) => (
                    <FormControlLabel
                      key={amenity}
                      control={
                        <Checkbox
                          checked={currentAccommodation.amenities?.includes(
                            amenity
                          )}
                          onChange={() => toggleAmenity(amenity)}
                        />
                      }
                      label={amenity}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
            <Popover
  open={Boolean(infoAnchorEl)}
  anchorEl={infoAnchorEl}
  onClose={() => {
    setInfoAnchorEl(null);
    setCurrentInfoKey(null);
  }}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
  PaperProps={{ sx: { p: 1.5, minWidth: 220, maxWidth: 300 } }}
>
  <Typography variant="body2" color="text.primary">
    {INFO_CONTENT[currentInfoKey] || "Field information"}
  </Typography>
</Popover>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetDialog} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSaveAccommodation} variant="contained">
              {editingIndex !== null ? "Update" : "Add"} Accommodation
            </Button>
          </DialogActions>
        </Dialog>
        <Button
          startIcon={<AddIcon />}
          onClick={openDialog}
          variant="contained"
          color="primary"
        >
          Add Accommodation
        </Button>
      </Box>

      {accommodations.length === 0 ? (
        <Card
          variant="outlined"
          sx={{ p: 4, textAlign: "center", borderStyle: "dashed" }}
        >
          <HomeIcon sx={{ fontSize: 48, color: "gray" }} />
          <Typography variant="body1" color="textSecondary">
            No accommodations added yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click "Add Accommodation" to get started
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {accommodations.map((acc, index) => (
            <Grid item xs={12} md={6} key={acc.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                    mb={2}
                  >
                    <Box>
                      <Typography variant="h6">{acc.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {
                          ACCOMMODATION_TYPES.find((t) => t.value === acc.type)
                            ?.label
                        }
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(index)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Quantity: {acc.quantity}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Max Occupancy: {acc.maxOccupancy} guests
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
  {acc.type === 'dormitory' 
    ? `${acc.bedsPerRoom || acc.maxOccupancy} Beds • ${acc.genderRestriction}` 
    : `${acc.maxOccupancy} guests`
}
</Typography>

                    <Typography variant="body2" color="textSecondary">
                      ₹{acc.pricePerNightB2C} per night
                    </Typography>

                    {/* Meal Plan Pricing on Card */}
{['CP', 'MAP', 'AP'].some(
  (plan) => acc.mealPlanPricing?.[plan]?.B2C
) && (
  <Box mt={0.5}>
    <Typography variant="body2" color="textSecondary" fontWeight={600}>
      Meal Plans:
    </Typography>
    {[
      { key: 'CP',  label: 'CP (Breakfast)' },
      { key: 'MAP', label: 'MAP (Breakfast + Dinner)' },
      { key: 'AP',  label: 'AP (All Meals)' },
    ].map(({ key, label }) =>
      acc.mealPlanPricing?.[key]?.B2C ? (
        <Typography key={key} variant="body2" color="textSecondary">
          {label}: ₹{acc.mealPlanPricing[key].B2C} / night
        </Typography>
      ) : null
    )}
  </Box>
)}
                    {acc.bedConfiguration && (
                      <Typography variant="body2" color="textSecondary">
                        {acc.bedConfiguration}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AccommodationsSection;
